/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/** Minimum number of characters before the header search shows suggestions (PRIC-66 CA1/CA2). */
export const SEARCH_PREVIEW_MIN_LENGTH = 3;

/** Number of products shown in the "Productos principales" section of the search dropdown. */
export const SEARCH_PREVIEW_PRODUCT_LIMIT = 3;

/** Maximum number of text suggestions shown in the "Sugerencias" section of the search dropdown. */
export const SEARCH_PREVIEW_SUGGESTION_LIMIT = 5;

/** A product card in the header search dropdown. */
export interface SearchPreviewProduct {
    id: string;
    name: string;
    url: string;
    brand?: string;
    categoryName?: string;
    image?: string;
    imageAlt?: string;
    currentPrice?: number;
    /** Only set when the product is on sale (list price above the current price). */
    listPrice?: number;
    /** Rounded discount percentage, only set together with `listPrice`. */
    discountPercent?: number;
    /** Stock at or below `global.inventory.lowStockThreshold` (same rule as the PDP inventory message). */
    lowStock?: boolean;
}

/** Payload returned by the `/resource/search-preview` endpoint. */
export interface SearchPreviewResult {
    query: string;
    searchPhrase: string;
    suggestions: string[];
    products: SearchPreviewProduct[];
    /** Total number of products matching the search phrase. */
    total: number;
    currency?: string;
}
